/**
 * Settings Page Component
 *
 * Main settings page for user preferences and configuration.
 * Currently includes digest notification preferences for coaches.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Header, DashboardLayout } from '@/components/shared';
import DigestPreferences from './DigestPreferences';
import SessionLoggingSettings from './SessionLoggingSettings';
import ReadinessAlertSettings from './ReadinessAlertSettings';
import PhoneSmsSettings from './PhoneSmsSettings';
import { getApiUrl } from '@/config/api';
import { authenticatedFetch } from '@/utils/apiUtils';
import { useModalSuccess } from '@/contexts/ModalContext';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Get user info from localStorage and parse the JSON
  const userInfoStr = localStorage.getItem('user_info');
  const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
  const userRole = userInfo?.role;
  const isCoach = userRole === 'coach' || userRole === 'admin';

  // State for user's teams
  const [userTeams, setUserTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);

  // Team visibility toggle state
  const [hideFromTeamViews, setHideFromTeamViews] = useState<boolean>(
    userInfo?.hide_from_team_views ?? true
  );
  const [savingVisibility, setSavingVisibility] = useState(false);

  /**
   * Fetch user's teams on component mount
   */
  useEffect(() => {
    if (isCoach) {
      fetchUserTeams();
    }
  }, [isCoach]);

  useModalSuccess(() => { if (isCoach) fetchUserTeams(); });

  /**
   * Toggle coach visibility in team views
   */
  const handleToggleVisibility = async () => {
    setSavingVisibility(true);
    const newValue = !hideFromTeamViews;
    try {
      const result = await authenticatedFetch(
        getApiUrl('/api/users/me/preferences/'),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hide_from_team_views: newValue }),
        },
        'updating team visibility'
      );
      if (result.success) {
        setHideFromTeamViews(newValue);
        // Update cached user info so other components pick it up
        const stored = localStorage.getItem('user_info');
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.hide_from_team_views = newValue;
          localStorage.setItem('user_info', JSON.stringify(parsed));
        }
        // Notify coach dashboard to force-refresh (backend caches were just invalidated)
        window.dispatchEvent(new Event('team-visibility-changed'));
      }
    } catch (error) {
      console.error('Error updating team visibility:', error);
    } finally {
      setSavingVisibility(false);
    }
  };

  /**
   * Fetch teams that the coach is a member of
   */
  const fetchUserTeams = async () => {
    try {
      const result = await authenticatedFetch(
        getApiUrl('/api/teams/teams/'),
        { method: 'GET' },
        'fetching user teams'
      );

      if (result.success && result.data) {
        // Handle custom pagination format {pagination: {...}, data: [...]}
        if (result.data.data && Array.isArray(result.data.data)) {
          setUserTeams(result.data.data);
        }
        // Handle DRF pagination format {results: [...]}
        else if (result.data.results && Array.isArray(result.data.results)) {
          setUserTeams(result.data.results);
        }
        // Handle direct array response
        else if (Array.isArray(result.data)) {
          setUserTeams(result.data);
        }
        // Fallback to empty array for any other structure (prevents crash)
        else {
          console.warn('Unexpected teams response format:', result.data);
          setUserTeams([]);
        }
      } else {
        // Explicitly set empty array on error
        setUserTeams([]);
        console.error('Error fetching teams:', result.error);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  return (
    <div className="min-h-screen cgm-bg-primary">
      {/* Header */}
      <Header
        userInfo={userInfo}
        isDark={isDark}
      />

      {/* Main Content */}
      <DashboardLayout>
        <div className="space-y-6">
          {/* Phone & SMS Section (All Users) */}
          <section className="cgm-card rounded-2xl p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold cgm-text-primary">
                Phone &amp; SMS
              </h2>
              <p className="mt-2 cgm-text-secondary">
                Add or update your phone number and manage SMS notifications from CGM Sports
              </p>
            </div>
            <PhoneSmsSettings
              userId={userInfo?.id}
              initialPhone={userInfo?.phone || ''}
              initialSmsOptIn={!!userInfo?.sms_opt_in}
            />
          </section>

          {/* Team Visibility Section (Coaches Only) */}
          {isCoach && (
            <section className="cgm-card rounded-2xl p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Team Visibility
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Control whether your wearable data appears alongside athletes in team views
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium cgm-text-primary">
                    Show my data in team views
                  </div>
                  <div className="text-xs cgm-text-secondary mt-1">
                    When enabled, your wearable data appears in team roster, readiness, and analytics
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleToggleVisibility}
                  disabled={savingVisibility}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    !hideFromTeamViews ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  } ${savingVisibility ? 'opacity-50' : ''}`}
                  aria-label="Toggle team visibility"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      !hideFromTeamViews ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </section>
          )}

          {/* Session Logging Settings Section (Coaches Only) */}
          {isCoach && (
            <section className={`cgm-card rounded-2xl p-6`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Session Logging Settings
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Configure when and who receives session logging reminders
                </p>
              </div>
              
              {loadingTeams ? (
                <div className="py-8 text-center cgm-text-secondary">
                  Loading teams...
                </div>
              ) : userTeams.length === 0 ? (
                <div className="py-8 text-center cgm-text-secondary">
                  No teams found. Create a team to configure session logging.
                </div>
              ) : (
                <div className="space-y-6">
                  {userTeams.map((team) => (
                    <SessionLoggingSettings
                      key={team.id}
                      teamId={team.id}
                      teamName={team.name}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Readiness Alerts Section (Coaches Only) */}
          {isCoach && (
            <section className={`cgm-card rounded-2xl p-6`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Readiness Alerts
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Get emailed when athletes' readiness scores drop below your threshold
                </p>
              </div>

              {loadingTeams ? (
                <div className="py-8 text-center cgm-text-secondary">
                  Loading teams...
                </div>
              ) : userTeams.length === 0 ? (
                <div className="py-8 text-center cgm-text-secondary">
                  No teams found. Create a team to configure readiness alerts.
                </div>
              ) : (
                <div className="space-y-6">
                  {userTeams.map((team) => (
                    <ReadinessAlertSettings
                      key={team.id}
                      teamId={team.id}
                      teamName={team.name}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Digest Notifications Section (Coaches Only) */}
          {isCoach && (
            <section className={`cgm-card rounded-2xl p-6`}>
              <div className="mb-6">
                <h2 className="text-2xl font-bold cgm-text-primary">
                  Digest Notifications
                </h2>
                <p className="mt-2 cgm-text-secondary">
                  Configure when and how you receive email digest updates
                </p>
              </div>
              <DigestPreferences />
            </section>
          )}

          {/* Future Settings Sections */}
          {/* Uncomment and expand as needed
          <section>
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Account Settings
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Manage your account information and preferences
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-500">Account settings coming soon...</p>
            </div>
          </section>
          */}
        </div>
      </DashboardLayout>
    </div>
  );
};

export default SettingsPage;
